import { Chart, registerables, type ChartConfiguration, type ChartDataset, type ChartType } from "chart.js";
import { onDispose } from "@/utils/component";
import { twColor } from "@/utils/style";
import { getAssetTypeSecondaryColor } from "@/data/assetMapping";
import { numberToHumanReadable } from "@/utils/formatting/number";
import { EAssetType } from "findef";

const humanAxisTick = (raw: number | string): string => {
  const n = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(n)) return String(raw);
  // numberToHumanReadable adds a space between the value and the suffix
  // ("45 M"); the axis looks cleaner without it ("45M"). 0 stays plain.
  if (n === 0) return "0";
  return numberToHumanReadable(n, 1, undefined, [], true, 1, true).replace(
    /\s+/g,
    "",
  );
};

Chart.register(...registerables);

type AIChartType = "line" | "bar" | "pie" | "doughnut";

export type IAIChartDataset = {
  label: string;
  data: Array<number>;
  backgroundColor?: Array<string>;
};

export type IAIChartData = {
  __chart: true;
  type: AIChartType;
  title?: string;
  labels: Array<string | number>;
  datasets: Array<IAIChartDataset>;
};

const PALETTE_KEYS = [
  "primary-500",
  "primary-300",
  "findex-primary-500",
  "primary-700",
  "primary-200",
  "primary-900",
];

const getPalette = (): Array<string> => PALETTE_KEYS.map((k) => twColor(k)).filter((c) => c.length > 0);

// Matches formatDiversificationLabel / formatAssetType in
// apps/backend/src/modules/assistant/tools/helpers.ts so diversification
// charts pick up the same asset-type palette used on the dashboard widget.
const LABEL_TO_ASSET_TYPE: Record<string, { type: EAssetType; listed?: boolean }> = {
  "Listed Equity": { type: EAssetType.EQUITY, listed: true },
  "Unlisted Equity": { type: EAssetType.EQUITY, listed: false },
  Equity: { type: EAssetType.EQUITY, listed: true },
  "Real Estate": { type: EAssetType.REAL_ESTATE },
  Alternative: { type: EAssetType.ALTERNATIVE },
  Crypto: { type: EAssetType.CRYPTO },
  Commodity: { type: EAssetType.COMMODITY },
  Cash: { type: EAssetType.CASH },
  Debt: { type: EAssetType.DEBT },
  Other: { type: EAssetType.UNDEFINED },
};

const resolveAssetTypeColor = (label: string | number): string | null => {
  const entry = LABEL_TO_ASSET_TYPE[String(label)];
  if (!entry) return null;
  return getAssetTypeSecondaryColor(entry.type, entry.listed || false);
};

const resolvePieSliceColors = (
  dataset: IAIChartDataset,
  labels: Array<string | number>,
  palette: Array<string>,
): Array<string> => {
  if (dataset.backgroundColor && dataset.backgroundColor.length === dataset.data.length) {
    return dataset.backgroundColor;
  }
  return dataset.data.map(
    (_, i) => resolveAssetTypeColor(labels[i] ?? "") || palette[i % palette.length] || "#888",
  );
};

const buildDatasets = (
  chartType: AIChartType,
  datasets: Array<IAIChartDataset>,
  labels: Array<string | number>,
  palette: Array<string>,
): Array<ChartDataset<ChartType>> => {
  if (chartType === "pie" || chartType === "doughnut") {
    return datasets.map((ds): ChartDataset<ChartType> => ({
      label: ds.label,
      data: ds.data,
      backgroundColor: resolvePieSliceColors(ds, labels, palette),
      borderWidth: 0,
    }));
  }

  return datasets.map((ds, i): ChartDataset<ChartType> => {
    const color = palette[i % palette.length] || "#888";
    return {
      label: ds.label,
      data: ds.data,
      backgroundColor: chartType === "bar" ? color : `${color}1f`,
      borderColor: color,
      borderWidth: chartType === "line" ? 1.75 : 2,
      pointRadius: 0,
      pointHoverRadius: chartType === "line" ? 4 : 0,
      pointBackgroundColor: color,
      pointBorderColor: "#fff",
      pointBorderWidth: 2,
      tension: chartType === "line" ? 0.3 : 0,
      fill: chartType === "line",
    } as ChartDataset<ChartType>;
  });
};

type LegendEntry = { label: string; color: string };

const buildLegendEntries = (
  chartType: AIChartType,
  datasets: Array<IAIChartDataset>,
  labels: Array<string | number>,
  palette: Array<string>,
): Array<LegendEntry> => {
  if (chartType === "pie" || chartType === "doughnut") {
    const ds = datasets[0];
    if (!ds) return [];
    const colors = resolvePieSliceColors(ds, labels, palette);
    return labels.map((label, i) => ({
      label: String(label),
      color: colors[i] || "#888",
    }));
  }

  if (datasets.length <= 1) return [];
  return datasets.map((ds, i) => ({
    label: ds.label,
    color: palette[i % palette.length] || "#888",
  }));
};

export const AIChart = defineComponent<{
  data: IAIChartData;
}>(
  (props) => {
    const canvasRef = ref<HTMLCanvasElement | null>(null);
    let chart: Chart | null = null;

    const render = () => {
      if (!canvasRef.value) return;
      if (chart) {
        chart.destroy();
        chart = null;
      }

      const palette = getPalette();
      const renderType: AIChartType = props.data.type === "doughnut" ? "pie" : props.data.type;
      const datasets = buildDatasets(renderType, props.data.datasets, props.data.labels, palette);

      const config: ChartConfiguration = {
        type: renderType,
        data: {
          labels: props.data.labels,
          datasets: datasets,
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          layout: {
            padding: renderType === "pie" ? 4 : 0,
          },
          plugins: {
            // We draw our own HTML legend above the canvas for tighter
            // typographic control; suppress Chart.js's built-in legend.
            legend: { display: false },
            tooltip: {
              backgroundColor: "rgba(17, 25, 40, 0.92)",
              titleFont: { family: "Inter", size: 11, weight: 500 },
              bodyFont: { family: "Inter", size: 11 },
              padding: 8,
              cornerRadius: 6,
              displayColors: false,
              callbacks:
                renderType === "line" || renderType === "bar"
                  ? {
                      label: (ctx) => {
                        const v = ctx.parsed.y;
                        const label = ctx.dataset.label || "";
                        const formatted = v == null ? "—" : humanAxisTick(v);
                        return label ? `${label}: ${formatted}` : formatted;
                      },
                    }
                  : undefined,
            },
          },
          scales:
            renderType === "line" || renderType === "bar"
              ? {
                  x: {
                    ticks: {
                      font: { family: "Inter", size: 10 },
                      color: twColor("gray-500") || "#6B7280",
                      autoSkip: true,
                      maxTicksLimit: 6,
                      maxRotation: 0,
                      minRotation: 0,
                    },
                    grid: { display: false },
                    border: { display: false },
                  },
                  y: {
                    ticks: {
                      font: { family: "Inter", size: 10 },
                      color: twColor("gray-500") || "#6B7280",
                      maxTicksLimit: 5,
                      callback: (value) => humanAxisTick(value as number),
                    },
                    grid: { color: twColor("gray-100") || "#F3F4F6" },
                    border: { display: false },
                  },
                }
              : undefined,
        },
      };

      chart = new Chart(canvasRef.value, config);
    };

    onMounted(() => {
      render();
    });

    watch(() => props.data, () => render(), { deep: true });

    onDispose(() => {
      if (chart) {
        chart.destroy();
        chart = null;
      }
    });

    return () => {
      const palette = getPalette();
      const renderType: AIChartType = props.data.type === "doughnut" ? "pie" : props.data.type;
      const legend = buildLegendEntries(renderType, props.data.datasets, props.data.labels, palette);
      const isRadial = renderType === "pie";

      return (
        <div class="w-full max-w-full min-w-0 bg-white rounded-xl border border-gray-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)] overflow-hidden">
          {props.data.title ? (
            <div class="relative px-5 pt-4 pb-3 border-b border-gray-100 bg-gradient-to-b from-gray-50/70 to-transparent">
              <span
                aria-hidden="true"
                class="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary-500/40 to-transparent"
              />
              <div class="inline-flex items-center gap-2">
                <span class="inline-block w-1.5 h-1.5 rounded-full bg-primary-500 shadow-[0_0_0_3px_rgba(63,131,248,0.12)]" />
                <div class="font-['Inter'] text-[17px] font-semibold tracking-[-0.01em] text-gray-900 break-words leading-snug">
                  {props.data.title}
                </div>
              </div>
            </div>
          ) : null}

          {legend.length > 0 ? (
            <div class="px-5 pt-3.5 flex flex-wrap items-center gap-x-4 gap-y-1.5">
              {legend.map((entry, i) => (
                <div
                  key={`legend-${i}-${entry.label}`}
                  class="inline-flex items-center gap-1.5 text-[11px] text-gray-600 font-['Inter'] leading-none"
                >
                  <span
                    class="inline-block w-1.5 h-1.5 rounded-full shrink-0"
                    style={{ background: entry.color }}
                  />
                  <span class="truncate max-w-[140px]">{entry.label}</span>
                </div>
              ))}
            </div>
          ) : null}

          <div
            class={[
              "w-full min-w-0 relative",
              isRadial ? "px-5 py-5" : "px-3 pb-3 pt-2",
            ]}
            style={{ height: isRadial ? "220px" : "240px" }}
          >
            <canvas ref={canvasRef} />
          </div>
        </div>
      );
    };
  },
  { props: ["data"] },
);
